/**
 * secAPI.js - SEC EDGAR Data Extraction and Financial Calculations
 * 
 * This module handles all data fetching and processing for CompareStock:
 * 1. API calls to SEC EDGAR (via backend proxy) for XBRL financial data
 * 2. API calls to Finnhub (via backend proxy) for stock quotes
 * 3. XBRL data extraction and normalization
 * 4. Financial ratio calculations
 * 
 * Key Concepts:
 * - XBRL: eXtensible Business Reporting Language - structured financial data in SEC filings
 * - us-gaap: US Generally Accepted Accounting Principles namespace
 * - ifrs-full: International Financial Reporting Standards namespace
 * - Point-in-time data: Balance sheet items (snapshot at period end)
 * - Period data: Income/Cash flow items (accumulated over 3 or 12 months)
 * 
 * Data Flow:
 * Frontend -> Backend Proxy -> SEC EDGAR API -> XBRL JSON -> Extracted Metrics
 */

const BACKEND_URL = 'https://comparestock-backend.onrender.com';

/**
 * IFRS_MAPPING - Maps US-GAAP concept names to their IFRS equivalents
 * 
 * Foreign filers (ADRs like TSM, ASML, BABA) report under IFRS instead of US-GAAP.
 * This mapping allows us to extract data from both reporting standards.
 * 
 * IMPORTANT: This is a SINGLE SOURCE OF TRUTH - do not duplicate this mapping elsewhere.
 */
const IFRS_MAPPING = {
  // Income Statement
  'RevenueFromContractWithCustomerExcludingAssessedTax': 'Revenue',
  'Revenues': 'Revenue',
  'SalesRevenueNet': 'Revenue',
  'CostOfGoodsAndServicesSold': 'CostOfSales',
  'CostOfRevenue': 'CostOfSales',
  'GrossProfit': 'GrossProfit',
  'ResearchAndDevelopmentExpense': 'ResearchAndDevelopmentExpense',
  'SellingAndMarketingExpense': 'SellingExpense',
  'GeneralAndAdministrativeExpense': 'AdministrativeExpense',
  'SellingGeneralAndAdministrativeExpense': 'SellingGeneralAndAdministrativeExpense',
  'OperatingExpenses': 'OtherExpenseByNature',
  'OperatingIncomeLoss': 'ProfitLossFromOperatingActivities',
  'InterestExpense': 'InterestExpense',
  'InterestIncomeExpenseNet': 'FinanceIncome',
  'OtherNonoperatingIncomeExpense': 'OtherIncomeExpense',
  'IncomeLossFromContinuingOperationsBeforeIncomeTaxesExtraordinaryItemsNoncontrollingInterest': 'ProfitLossBeforeTax',
  'IncomeTaxExpenseBenefit': 'IncomeTaxExpenseContinuingOperations',
  'NetIncomeLoss': 'ProfitLoss',
  'NetIncomeLossAvailableToCommonStockholdersBasic': 'ProfitLoss',
  'EarningsPerShareBasic': 'BasicEarningsLossPerShare',
  'EarningsPerShareDiluted': 'DilutedEarningsLossPerShare',
  'WeightedAverageNumberOfSharesOutstandingBasic': 'WeightedAverageShares',
  'WeightedAverageNumberOfDilutedSharesOutstanding': 'AdjustedWeightedAverageShares',
  
  // Balance Sheet - Assets
  'CashAndCashEquivalentsAtCarryingValue': 'CashAndCashEquivalents',
  'CashCashEquivalentsRestrictedCashAndRestrictedCashEquivalents': 'CashAndCashEquivalents',
  'Cash': 'CashAndCashEquivalents',
  'MarketableSecuritiesCurrent': 'CurrentFinancialAssetsAtFairValueThroughProfitOrLoss',
  'AccountsReceivableNetCurrent': 'TradeAndOtherCurrentReceivables',
  'InventoryNet': 'Inventories',
  'PrepaidExpenseAndOtherAssetsCurrent': 'OtherCurrentAssets',
  'AssetsCurrent': 'CurrentAssets',
  'PropertyPlantAndEquipmentNet': 'PropertyPlantAndEquipment',
  'OperatingLeaseRightOfUseAsset': 'RightofuseAssets',
  'Goodwill': 'Goodwill',
  'IntangibleAssetsNetExcludingGoodwill': 'IntangibleAssetsOtherThanGoodwill',
  'OtherAssetsNoncurrent': 'OtherNoncurrentAssets',
  'AssetsNoncurrent': 'NoncurrentAssets',
  'Assets': 'Assets',
  
  // Balance Sheet - Liabilities
  'AccountsPayableCurrent': 'TradeAndOtherCurrentPayables',
  'AccruedLiabilitiesCurrent': 'OtherCurrentLiabilities',
  'ContractWithCustomerLiabilityCurrent': 'ContractLiabilities',
  'OperatingLeaseLiabilityCurrent': 'CurrentLeaseLiabilities',
  'LiabilitiesCurrent': 'CurrentLiabilities',
  'LongTermDebtNoncurrent': 'NoncurrentPortionOfNoncurrentBorrowings',
  'LongTermDebt': 'NoncurrentPortionOfNoncurrentBorrowings',
  'OperatingLeaseLiabilityNoncurrent': 'NoncurrentLeaseLiabilities',
  'OtherLiabilitiesNoncurrent': 'OtherNoncurrentLiabilities',
  'LiabilitiesNoncurrent': 'NoncurrentLiabilities',
  'Liabilities': 'Liabilities',
  
  // Balance Sheet - Equity
  'CommonStockValue': 'IssuedCapital',
  'AdditionalPaidInCapital': 'SharePremium',
  'RetainedEarningsAccumulatedDeficit': 'RetainedEarnings',
  'AccumulatedOtherComprehensiveIncomeLossNetOfTax': 'OtherReserves',
  'StockholdersEquity': 'Equity',
  'StockholdersEquityIncludingPortionAttributableToNoncontrollingInterest': 'Equity',
  'LiabilitiesAndStockholdersEquity': 'EquityAndLiabilities',
  
  // Cash Flow
  'DepreciationDepletionAndAmortization': 'DepreciationAndAmortisationExpense',
  'DepreciationAndAmortization': 'DepreciationAndAmortisationExpense',
  'Depreciation': 'DepreciationAndAmortisationExpense',
  'ShareBasedCompensation': 'SharebasedPaymentExpense',
  'AmortizationOfIntangibleAssets': 'AmortisationExpense',
  'DeferredIncomeTaxExpenseBenefit': 'DeferredTaxExpenseIncome',
  'NetCashProvidedByUsedInOperatingActivities': 'CashFlowsFromUsedInOperatingActivities',
  'NetCashProvidedByUsedInOperatingActivitiesContinuingOperations': 'CashFlowsFromUsedInOperatingActivities',
  'PaymentsToAcquirePropertyPlantAndEquipment': 'PurchaseOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities',
  'PaymentsToAcquireProductiveAssets': 'PurchaseOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities',
  'PaymentsToAcquireMarketableSecurities': 'PurchaseOfInvestments',
  'ProceedsFromSaleAndMaturityOfMarketableSecurities': 'ProceedsFromSalesOfInvestments',
  'PaymentsToAcquireBusinessesNetOfCashAcquired': 'CashFlowsUsedInObtainingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities',
  'NetCashProvidedByUsedInInvestingActivities': 'CashFlowsFromUsedInInvestingActivities',
  'ProceedsFromIssuanceOfCommonStock': 'ProceedsFromIssuingShares',
  'PaymentsForRepurchaseOfCommonStock': 'PaymentsToAcquireOrRedeemEntitysShares',
  'PaymentsOfDividends': 'DividendsPaid',
  'ProceedsFromIssuanceOfLongTermDebt': 'ProceedsFromBorrowingsClassifiedAsFinancingActivities',
  'RepaymentsOfLongTermDebt': 'RepaymentsOfBorrowingsClassifiedAsFinancingActivities',
  'NetCashProvidedByUsedInFinancingActivities': 'CashFlowsFromUsedInFinancingActivities',
  
  // Shares
  'CommonStockSharesOutstanding': 'NumberOfSharesIssued',
  'CommonStockSharesIssued': 'NumberOfSharesIssued',
  'SharesOutstanding': 'NumberOfSharesIssued',
};

/**
 * Fetches company financial facts from SEC EDGAR via backend proxy
 * @param {string} ticker - Stock ticker symbol (e.g., 'AAPL', 'MSFT')
 * @returns {Promise<Object>} SEC EDGAR companyfacts JSON containing all XBRL data
 * @throws {Error} If company not found or API fails
 */
export async function getCompanyFacts(ticker) {
  const response = await fetch(`${BACKEND_URL}/api/company/${ticker}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to fetch data for ${ticker}`);
  }
  
  return response.json();
}

/**
 * Fetches current stock quote from Finnhub via backend proxy
 * @param {string} ticker - Stock ticker symbol
 * @returns {Promise<Object>} Quote object with { price, change, percentChange }
 */
export async function getStockQuote(ticker) {
  const response = await fetch(`${BACKEND_URL}/api/quote/${ticker}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to fetch quote for ${ticker}`);
  }
  
  return response.json();
}

/**
 * Fetches company profile from Finnhub via backend proxy
 * @param {string} ticker - Stock ticker symbol
 * @returns {Promise<Object>} Profile with { industry, sector, country, etc. }
 */
export async function getCompanyProfile(ticker) {
  const response = await fetch(`${BACKEND_URL}/api/profile/${ticker}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to fetch profile for ${ticker}`);
  }
  
  return response.json();
}

// Fetch filing presentation structure (line items in SEC-defined order)
export async function getFilingStructure(ticker, formType = '10-Q') {
  try {
    const response = await fetch(`${BACKEND_URL}/api/filing-structure/${ticker}?form=${formType}`);
    if (!response.ok) return null;
    return response.json();
  } catch (e) {
    console.warn(`[FILING] Could not fetch structure for ${ticker}:`, e.message);
    return null;
  }
}

/**
 * Extracts key financial metrics from SEC XBRL data for valuation calculations
 * 
 * This function handles both US-GAAP and IFRS reporting standards by mapping
 * equivalent concepts. It extracts TTM (Trailing Twelve Months) values for
 * income/cash flow items and point-in-time values for balance sheet items.
 * 
 * @param {Object} facts - SEC EDGAR companyfacts JSON
 * @param {string} periodType - 'annual' or 'quarterly'
 * @returns {Object} Extracted metrics for valuation calculations:
 *   - revenue, netIncome, grossProfit, operatingIncome (TTM values)
 *   - totalAssets, totalLiabilities, cash, equity (point-in-time)
 *   - sharesOutstanding, bookValue, freeCashFlow, etc.
 */
export function extractValuationInputs(facts, periodType = 'annual') {
  // Pull from the SAME extractStatementData that the statement tabs use
  // This guarantees valuation ratios match the displayed financial statements
  const incomeRows = extractStatementData(facts, 'income', 'annual');
  const balanceRows = extractStatementData(facts, 'balance', 'annual');
  const cashflowRows = extractStatementData(facts, 'cashflow', 'annual');
  
  // Helper: get most recent value from a row by label
  const getVal = (rows, label) => {
    const row = rows.find(r => r.label === label);
    if (row && row.quarters && row.quarters.length > 0) {
      return row.quarters[0]?.value ?? null;
    }
    return null;
  };
  
  // Income Statement
  const revenue = getVal(incomeRows, 'Total Revenue');
  const netIncome = getVal(incomeRows, 'Net Income');
  const eps = getVal(incomeRows, 'Basic EPS');
  const operatingIncome = getVal(incomeRows, 'Operating Income');
  const grossProfit = getVal(incomeRows, 'Gross Profit');
  
  // Balance Sheet
  const cash = getVal(balanceRows, 'Cash & Equivalents');
  const totalAssets = getVal(balanceRows, 'Total Assets');
  const totalLiabilities = getVal(balanceRows, 'Total Liabilities');
  const bookValue = getVal(balanceRows, 'Total Equity');
  const currentAssets = getVal(balanceRows, 'Total Current Assets');
  const currentLiabilities = getVal(balanceRows, 'Total Current Liabilities');
  const longTermDebt = getVal(balanceRows, 'Long-Term Debt');
  
  // Cash Flow Statement
  const operatingCashFlow = getVal(cashflowRows, 'Cash from Operations');
  const capex = getVal(cashflowRows, 'Capital Expenditures');
  const depreciation = getVal(cashflowRows, 'Depreciation & Amortization');
  
  // Derived metrics
  const freeCashFlow = operatingCashFlow != null ? operatingCashFlow - Math.abs(capex || 0) : null;
  const ebitda = (operatingIncome != null && depreciation != null) ? operatingIncome + depreciation : null;
  const totalDebt = longTermDebt; // Use long-term debt as proxy for total debt
  
  // Shares: prefer basic weighted average from income statement (most universal)
  const sharesOutstanding = getVal(incomeRows, 'Shares Outstanding (Basic)');
  
  // Fiscal Year: derive from the income statement's most recent period
  let fiscalYear = null;
  const revenueRow = incomeRows.find(r => r.label === 'Total Revenue') || 
                     incomeRows.find(r => r.label === 'Net Income');
  if (revenueRow && revenueRow.quarters && revenueRow.quarters.length > 0) {
    const period = revenueRow.quarters[0]?.period;
    if (period) {
      // period is like "2025-12-31" or "2025-Q4"
      const year = period.substring(0, 4);
      fiscalYear = `FY${year}`;
    }
  }

  return {
    sharesOutstanding,
    revenue,
    netIncome,
    eps,
    bookValue,
    freeCashFlow,
    operatingCashFlow,
    ebitda,
    operatingIncome,
    grossProfit,
    cash,
    totalDebt: totalDebt > 0 ? totalDebt : null,
    totalAssets,
    totalLiabilities,
    currentAssets,
    currentLiabilities,
    fiscalYear,
    depreciation,
    filingDates: {} // Simplified - no longer tracking per-field filing dates
  };
}
// Calculate valuation ratios given price and SEC fundamentals
export function calculateValuationRatios(price, inputs, finnhubMarketCap = null) {
  const { 
    sharesOutstanding, 
    revenue, 
    netIncome, 
    eps, 
    bookValue, 
    freeCashFlow,
    operatingCashFlow,
    ebitda,
    operatingIncome,
    cash,
    totalDebt,
    totalAssets,
    totalLiabilities,
    grossProfit,
    currentAssets,
    currentLiabilities,
    fiscalYear
  } = inputs;
  
  // Market Cap - prefer Finnhub's value (more accurate, real-time)
  // Only fall back to calculated if Finnhub doesn't provide it
  const marketCap = finnhubMarketCap || ((price && sharesOutstanding) ? price * sharesOutstanding : null);
  
  // Enterprise Value = Market Cap + Total Debt - Cash
  const enterpriseValue = marketCap ? marketCap + (totalDebt || 0) - (cash || 0) : null;
  
  // ========== VALUATION RATIOS ==========
  
  // P/E Ratio
  const peRatio = (price && eps && eps !== 0) ? price / eps : null;
  
  // P/S Ratio (Price to Sales)
  const psRatio = (marketCap && revenue && revenue !== 0) ? marketCap / revenue : null;
  
  // P/B Ratio (Price to Book)
  const pbRatio = (marketCap && bookValue && bookValue !== 0) ? marketCap / bookValue : null;
  
  // P/FCF Ratio (Price to Free Cash Flow)
  const pfcfRatio = (marketCap && freeCashFlow && freeCashFlow > 0) ? marketCap / freeCashFlow : null;
  
  // EV/Revenue
  const evRevenue = (enterpriseValue && revenue && revenue !== 0) ? enterpriseValue / revenue : null;
  
  // EV/EBITDA
  const evEbitda = (enterpriseValue && ebitda && ebitda > 0) ? enterpriseValue / ebitda : null;
  
  // EV/FCF
  const evFcf = (enterpriseValue && freeCashFlow && freeCashFlow > 0) ? enterpriseValue / freeCashFlow : null;
  
  // ========== YIELD METRICS ==========
  
  // FCF Yield (inverse of P/FCF, as percentage)
  const fcfYield = (freeCashFlow && marketCap && marketCap !== 0) ? (freeCashFlow / marketCap) * 100 : null;
  
  // Earnings Yield (inverse of P/E, as percentage)
  const earningsYield = (netIncome && marketCap && marketCap !== 0) ? (netIncome / marketCap) * 100 : null;
  
  // ========== PER SHARE METRICS ==========
  
  // Revenue Per Share
  const revenuePerShare = (revenue && sharesOutstanding) ? revenue / sharesOutstanding : null;
  
  // Book Value Per Share
  const bookValuePerShare = (bookValue && sharesOutstanding) ? bookValue / sharesOutstanding : null;
  
  // FCF Per Share
  const fcfPerShare = (freeCashFlow && sharesOutstanding) ? freeCashFlow / sharesOutstanding : null;
  
  // Cash Per Share
  const cashPerShare = (cash && sharesOutstanding) ? cash / sharesOutstanding : null;
  
  // ========== PROFITABILITY METRICS ==========
  
  // Gross Margin %
  const grossMargin = (grossProfit && revenue && revenue !== 0) ? (grossProfit / revenue) * 100 : null;
  
  // Operating Margin %
  const operatingMargin = (operatingIncome && revenue && revenue !== 0) ? (operatingIncome / revenue) * 100 : null;
  
  // Net Profit Margin %
  const netMargin = (netIncome && revenue && revenue !== 0) ? (netIncome / revenue) * 100 : null;
  
  // ROE (Return on Equity) %
  const roe = (netIncome && bookValue && bookValue !== 0) ? (netIncome / bookValue) * 100 : null;
  
  // ROA (Return on Assets) %
  const roa = (netIncome && totalAssets && totalAssets !== 0) ? (netIncome / totalAssets) * 100 : null;
  
  // ========== FINANCIAL HEALTH ==========
  
  // Debt to Equity Ratio
  const debtToEquity = (totalDebt && bookValue && bookValue !== 0) ? totalDebt / bookValue : null;
  
  // Current Ratio
  const currentRatio = (currentAssets && currentLiabilities && currentLiabilities !== 0) ? currentAssets / currentLiabilities : null;
  
  // Net Debt
  const netDebt = (totalDebt !== null || cash !== null) ? (totalDebt || 0) - (cash || 0) : null;
  
  // Net Debt / EBITDA
  const netDebtToEbitda = (netDebt !== null && ebitda && ebitda > 0) ? netDebt / ebitda : null;
  
  return {
    // Price & Market
    price,
    marketCap,
    enterpriseValue,
    
    // Valuation Ratios
    peRatio,
    psRatio,
    pbRatio,
    pfcfRatio,
    evRevenue,
    evEbitda,
    evFcf,
    
    // Yields
    fcfYield,
    earningsYield,
    
    // Per Share
    eps,
    revenuePerShare,
    bookValuePerShare,
    fcfPerShare,
    cashPerShare,
    
    // Profitability
    grossMargin,
    operatingMargin,
    netMargin,
    roe,
    roa,
    
    // Financial Health
    debtToEquity,
    currentRatio,
    netDebt,
    netDebtToEbitda,
    
    // Fiscal Year indicator
    fiscalYear,
    
    // Raw inputs for reference
    sharesOutstanding,
    revenue,
    netIncome,
    bookValue,
    freeCashFlow,
    ebitda,
    cash,
    totalDebt
  };
}

// Ticker to primary industry mapping
const TICKER_INDUSTRY_MAP = {
  // Interactive Media & Services
  'META': 'interactive-media',
  'GOOGL': 'interactive-media',
  'GOOG': 'interactive-media',
  'SNAP': 'interactive-media',
  'PINS': 'interactive-media',
  'TWTR': 'interactive-media',
  
  // Software - Application
  'ASAN': 'software-application',
  'DOCU': 'software-application',
  'CRM': 'software-application',
  'WDAY': 'software-application',
  'ZM': 'software-application',
  'TEAM': 'software-application',
  'SHOP': 'software-application',
  
  // Software - Infrastructure
  'MSFT': 'software-infrastructure',
  'ORCL': 'software-infrastructure',
  'NOW': 'software-infrastructure',
  'SNOW': 'software-infrastructure',
  'MDB': 'software-infrastructure',
  'DDOG': 'software-infrastructure',
  
  // E-Commerce
  'AMZN': 'e-commerce',
  'EBAY': 'e-commerce',
  'ETSY': 'e-commerce',
  'BABA': 'e-commerce',
  
  // Consumer Electronics
  'AAPL': 'consumer-electronics',
  
  // Semiconductors
  'NVDA': 'semiconductors',
  'AMD': 'semiconductors',
  'INTC': 'semiconductors',
  
  // Streaming / Entertainment
  'NFLX': 'streaming',
  'DIS': 'streaming',
  'ROKU': 'streaming',
  
  // Electric Vehicles
  'TSLA': 'electric-vehicles',
  'RIVN': 'electric-vehicles',
  'LCID': 'electric-vehicles',
};

// Industry display names
const INDUSTRY_NAMES = {
  'interactive-media': 'Interactive Media & Services',
  'software-application': 'Software - Application',
  'software-infrastructure': 'Software - Infrastructure',
  'e-commerce': 'E-Commerce',
  'consumer-electronics': 'Consumer Electronics',
  'semiconductors': 'Semiconductors',
  'streaming': 'Streaming & Entertainment',
  'electric-vehicles': 'Electric Vehicles',
  'unknown': 'Unknown Industry',
};

// Industry benchmark averages (in percentages for margins, ratios for valuation)
// These are approximate median values for each industry
const INDUSTRY_BENCHMARKS = {
  'interactive-media': {
    // Margin benchmarks (%)
    grossMargin: 65,
    operatingMargin: 25,
    rdPercent: 20,
    smPercent: 15,
    gaPercent: 10,
    // Valuation benchmarks (ratios)
    peRatio: 25,
    psRatio: 6,
    pbRatio: 4,
    pfcfRatio: 20,
    fcfYield: 5,
    evEbitda: 15,
  },
  'software-application': {
    grossMargin: 75,
    operatingMargin: -5,
    rdPercent: 25,
    smPercent: 35,
    gaPercent: 15,
    peRatio: 35,
    psRatio: 8,
    pbRatio: 10,
    pfcfRatio: 30,
    fcfYield: 3,
    evEbitda: 25,
  },
  'software-infrastructure': {
    grossMargin: 70,
    operatingMargin: 15,
    rdPercent: 20,
    smPercent: 25,
    gaPercent: 10,
    peRatio: 40,
    psRatio: 10,
    pbRatio: 12,
    pfcfRatio: 35,
    fcfYield: 3,
    evEbitda: 30,
  },
  'e-commerce': {
    grossMargin: 40,
    operatingMargin: 5,
    rdPercent: 10,
    smPercent: 8,
    gaPercent: 5,
    peRatio: 30,
    psRatio: 2,
    pbRatio: 8,
    pfcfRatio: 25,
    fcfYield: 4,
    evEbitda: 15,
  },
  'consumer-electronics': {
    grossMargin: 43,
    operatingMargin: 30,
    rdPercent: 7,
    smPercent: 6,
    gaPercent: 3,
    peRatio: 28,
    psRatio: 7,
    pbRatio: 45,
    pfcfRatio: 25,
    fcfYield: 4,
    evEbitda: 20,
  },
  'semiconductors': {
    grossMargin: 60,
    operatingMargin: 35,
    rdPercent: 20,
    smPercent: 5,
    gaPercent: 5,
    peRatio: 25,
    psRatio: 10,
    pbRatio: 15,
    pfcfRatio: 30,
    fcfYield: 3,
    evEbitda: 18,
  },
  'streaming': {
    grossMargin: 40,
    operatingMargin: 10,
    rdPercent: 10,
    smPercent: 15,
    gaPercent: 8,
    peRatio: 35,
    psRatio: 4,
    pbRatio: 6,
    pfcfRatio: 25,
    fcfYield: 4,
    evEbitda: 15,
  },
  'electric-vehicles': {
    grossMargin: 20,
    operatingMargin: -10,
    rdPercent: 10,
    smPercent: 5,
    gaPercent: 8,
    peRatio: 50,
    psRatio: 5,
    pbRatio: 10,
    pfcfRatio: 40,
    fcfYield: 2,
    evEbitda: 30,
  },
  'unknown': {
    grossMargin: 50,
    operatingMargin: 10,
    rdPercent: 15,
    smPercent: 15,
    gaPercent: 10,
    peRatio: 20,
    psRatio: 3,
    pbRatio: 3,
    pfcfRatio: 15,
    fcfYield: 5,
    evEbitda: 12,
  },
};

// Get industry for a ticker
export function getTickerIndustry(ticker) {
  const industry = TICKER_INDUSTRY_MAP[ticker.toUpperCase()] || 'unknown';
  return {
    id: industry,
    name: INDUSTRY_NAMES[industry],
  };
}

// Get benchmark for an industry
export function getIndustryBenchmark(industryId) {
  return INDUSTRY_BENCHMARKS[industryId] || INDUSTRY_BENCHMARKS['unknown'];
}

// Metrics that can be compared to industry benchmarks
// Maps SEC concept to benchmark key
const BENCHMARK_METRIC_MAP = {
  'GrossProfit': 'grossMargin',
  'OperatingIncomeLoss': 'operatingMargin',
  'ResearchAndDevelopmentExpense': 'rdPercent',
  'SellingAndMarketingExpense': 'smPercent',
  'GeneralAndAdministrativeExpense': 'gaPercent',
  'SellingGeneralAndAdministrativeExpense': 'sgaPercent', // Combined S&M + G&A
};

// Check if a concept can be compared to industry benchmark
export function getBenchmarkKey(concept) {
  return BENCHMARK_METRIC_MAP[concept] || null;
}

// Condensed Consolidated Statement line items - exact SEC XBRL concept names
// These are the ~15-20 items that appear in typical 10-Q condensed statements
// We show these if they exist, skip if not. No calculations, no heuristics.
// Structure: isHeader = section header, isSubtotal = section total, isTotal = grand total
// sectionEnd = this subtotal ends a collapsible section
// Check if company data indicates a foreign issuer (files 6-K/20-F instead of 10-Q/10-K)
export function isForeignIssuer(facts) {
  // Foreign issuers typically have ifrs data instead of us-gaap
  const hasIfrs = facts.facts?.['ifrs-full'] && Object.keys(facts.facts['ifrs-full']).length > 0;
  const hasUsGaap = facts.facts?.['us-gaap'] && Object.keys(facts.facts['us-gaap']).length > 0;
  
  // If they have IFRS but no US-GAAP, they're likely a foreign issuer
  if (hasIfrs && !hasUsGaap) return true;
  
  // Check if any data exists at all
  if (!hasUsGaap && !hasIfrs) return null; // No data at all
  
  return false;
}

// Get company info from facts
export function getCompanyInfo(facts) {
  return {
    name: facts.entityName || 'Unknown',
    cik: facts.cik || '',
    isForeign: isForeignIssuer(facts)
  };
}


// ============================================================================
// FRAME-BASED EXTRACTION
// 
// The SEC companyfacts API includes a `frame` field on each value that 
// classifies it: CY2025Q3 = Q3 2025 standalone, CY2024 = FY 2024,
// CY2025Q3I = balance sheet at Q3 2025 end.
//
// We scan ALL concepts for values matching target frames.
// Anchor concepts provide ordering; everything else sorted by materiality.
// ============================================================================

// Get all available frames from a company's data
function getAvailableFramesFromFacts(facts) {
  const allFacts = facts.facts || {};
  const quarterlyFrames = new Set();
  const annualFrames = new Set();
  const instantFrames = new Set();
  
  for (const [namespace, concepts] of Object.entries(allFacts)) {
    if (namespace === 'dei') continue;
    for (const [, conceptData] of Object.entries(concepts)) {
      if (!conceptData?.units) continue;
      for (const values of Object.values(conceptData.units)) {
        for (const v of values) {
          if (!v.frame) continue;
          if (/^CY\d{4}Q[1-4]I$/.test(v.frame)) instantFrames.add(v.frame);
          else if (/^CY\d{4}Q[1-4]$/.test(v.frame)) quarterlyFrames.add(v.frame);
          else if (/^CY\d{4}I$/.test(v.frame)) instantFrames.add(v.frame);
          else if (/^CY\d{4}$/.test(v.frame)) annualFrames.add(v.frame);
        }
      }
    }
  }
  
  return { quarterlyFrames, annualFrames, instantFrames };
}

// Convert frame string to display period date
function frameToPeriod(frame) {
  const qMatch = frame.match(/^CY(\d{4})Q(\d)I?$/);
  if (qMatch) {
    const year = qMatch[1];
    const q = parseInt(qMatch[2]);
    const monthEnd = { 1: '03-31', 2: '06-30', 3: '09-30', 4: '12-31' };
    return `${year}-${monthEnd[q]}`;
  }
  const yMatch = frame.match(/^CY(\d{4})I?$/);
  if (yMatch) return `${yMatch[1]}-12-31`;
  return frame;
}

// ============================================================================
// THREE-TIER CLASSIFICATION SYSTEM
//
// Tier 1: Exact concept match (anchors) — 100% accurate, covers common tags
// Tier 2: Keyword match WITH exclusions — catches industry variants safely
// Tier 3: "Other Reported Items" — anything unclassified, no risk of error
//
// Philosophy: Never miscategorize. It's OK for an item to land in "Other"
// rather than be placed in the wrong bucket. This covers 90-95% of cases.
// ============================================================================

// Tier 1: Exact concept anchors — highest confidence, zero risk
// These are the most common XBRL tags across all standard filers
const INCOME_ANCHORS = [
  { concepts: ['Revenues', 'RevenueFromContractWithCustomerExcludingAssessedTax', 'RevenueFromContractWithCustomerIncludingAssessedTax', 'SalesRevenueNet', 'RevenuesNetOfInterestExpense', 'NoninterestIncome', 'InterestAndDividendIncomeOperating', 'Revenue'], label: 'Total Revenue', section: 'revenue', isBold: true, order: 10 },
  { concepts: ['CostOfGoodsAndServicesSold', 'CostOfRevenue', 'CostOfSales'], label: 'Cost of Revenue', section: 'costs', order: 20 },
  { concepts: ['GrossProfit'], label: 'Gross Profit', section: 'gross', isBold: true, order: 30 },
  { concepts: ['ResearchAndDevelopmentExpense', 'ResearchAndDevelopmentExpenseExcludingAcquiredInProcessCost'], label: 'Research & Development', section: 'opex', order: 40 },
  { concepts: ['SellingGeneralAndAdministrativeExpense', 'GeneralAndAdministrativeExpense', 'SellingAndMarketingExpense', 'SellingExpense'], label: 'SG&A', section: 'opex', order: 42 },
  { concepts: ['LaborAndRelatedExpense'], label: 'Compensation & Benefits', section: 'opex', order: 44 },
  { concepts: ['OperatingExpenses'], label: 'Total Operating Expenses', section: 'opex', isBold: true, order: 50 },
  { concepts: ['OperatingIncomeLoss', 'ProfitLossFromOperatingActivities'], label: 'Operating Income', section: 'operating', isBold: true, order: 60 },
  { concepts: ['NonoperatingIncomeExpense'], label: 'Non-Operating Income/Expense', section: 'nonop', order: 70 },
  { concepts: ['InterestExpense'], label: 'Interest Expense', section: 'nonop', order: 71 },
  { concepts: ['InterestIncomeExpenseNet', 'InterestRevenueExpense'], label: 'Interest Income, Net', section: 'nonop', order: 72 },
  { concepts: ['OtherNonoperatingIncomeExpense'], label: 'Other Non-Operating Income/Expense', section: 'nonop', order: 73 },
  { concepts: ['IncomeLossFromContinuingOperationsBeforeIncomeTaxesExtraordinaryItemsNoncontrollingInterest', 'IncomeLossFromContinuingOperationsBeforeIncomeTaxesMinorityInterestAndIncomeLossFromEquityMethodInvestments', 'ProfitLossBeforeTax'], label: 'Income Before Taxes', section: 'pretax', isBold: true, order: 80 },
  { concepts: ['IncomeTaxExpenseBenefit', 'IncomeTaxExpenseContinuingOperations'], label: 'Income Tax', section: 'tax', order: 90 },
  { concepts: ['NetIncomeLoss', 'ProfitLoss', 'NetIncomeLossAvailableToCommonStockholdersBasic'], label: 'Net Income', section: 'net', isBold: true, order: 100 },
  { concepts: ['EarningsPerShareBasic'], label: 'Basic EPS', section: 'eps', isPerShare: true, order: 110 },
  { concepts: ['EarningsPerShareDiluted'], label: 'Diluted EPS', section: 'eps', isPerShare: true, order: 111 },
  { concepts: ['WeightedAverageNumberOfSharesOutstandingBasic'], label: 'Shares Outstanding (Basic)', section: 'shares', isShares: true, order: 120 },
  { concepts: ['WeightedAverageNumberOfDilutedSharesOutstanding'], label: 'Shares Outstanding (Diluted)', section: 'shares', isShares: true, order: 121 },
];

const BALANCE_ANCHORS = [
  { concepts: ['CashAndCashEquivalentsAtCarryingValue', 'CashAndCashEquivalentsFairValueDisclosure', 'Cash', 'CashCashEquivalentsRestrictedCashAndRestrictedCashEquivalents', 'CashCashEquivalentsAndShortTermInvestments'], label: 'Cash & Equivalents', section: 'currentAssets', order: 10, keywords: ['CashAndCashEquivalent', 'CashCashEquivalent'] },
  { concepts: ['ShortTermInvestments', 'MarketableSecuritiesCurrent', 'AvailableForSaleSecuritiesCurrent', 'OtherShortTermInvestments', 'HeldToMaturitySecuritiesCurrent'], label: 'Short-Term Investments', section: 'currentAssets', order: 11, keywords: ['ShortTermInvestment', 'MarketableSecurities'] },
  { concepts: ['AccountsReceivableNetCurrent', 'AccountsReceivableNet', 'ReceivablesNetCurrent', 'TradeAndOtherCurrentReceivables'], label: 'Accounts Receivable', section: 'currentAssets', order: 12, keywords: ['ReceivableNet', 'AccountsReceivable', 'ReceivablesCurrent'] },
  { concepts: ['InventoryNet', 'InventoryGross', 'Inventories'], label: 'Inventory', section: 'currentAssets', order: 13, keywords: ['Inventory'] },
  { concepts: ['AssetsCurrent'], label: 'Total Current Assets', section: 'currentAssets', isBold: true, order: 20 },
  { concepts: ['PropertyPlantAndEquipmentNet', 'PropertyPlantAndEquipmentAndFinanceLeaseRightOfUseAssetAfterAccumulatedDepreciationAndAmortization'], label: 'Property & Equipment', section: 'ncAssets', order: 30, keywords: ['PropertyPlantAndEquipment'] },
  { concepts: ['Goodwill'], label: 'Goodwill', section: 'ncAssets', order: 31 },
  { concepts: ['IntangibleAssetsNetExcludingGoodwill'], label: 'Intangible Assets', section: 'ncAssets', order: 32 },
  { concepts: ['OperatingLeaseRightOfUseAsset'], label: 'Operating Lease Assets', section: 'ncAssets', order: 33 },
  { concepts: ['Assets'], label: 'Total Assets', section: 'totalAssets', isBold: true, order: 40 },
  { concepts: ['AccountsPayableCurrent', 'AccountsPayableAndAccruedLiabilitiesCurrent'], label: 'Accounts Payable', section: 'currentLiab', order: 50, keywords: ['AccountsPayable'] },
  { concepts: ['LiabilitiesCurrent'], label: 'Total Current Liabilities', section: 'currentLiab', isBold: true, order: 60 },
  { concepts: ['LongTermDebtNoncurrent', 'LongTermDebt', 'LongTermDebtAndCapitalLeaseObligations'], label: 'Long-Term Debt', section: 'ncLiab', order: 70, keywords: ['LongTermDebt'] },
  { concepts: ['LiabilitiesNoncurrent'], label: 'Non-Current Liabilities', section: 'ncLiab', order: 75 },
  { concepts: ['Liabilities'], label: 'Total Liabilities', section: 'totalLiab', isBold: true, order: 80 },
  { concepts: ['StockholdersEquity', 'StockholdersEquityIncludingPortionAttributableToNoncontrollingInterest'], label: 'Total Equity', section: 'equity', isBold: true, order: 90 },
  { concepts: ['LiabilitiesAndStockholdersEquity'], label: 'Total Liabilities & Equity', section: 'total', isBold: true, order: 100 },
  { concepts: ['CommonStockSharesOutstanding'], label: 'Shares Outstanding', section: 'shares', isShares: true, order: 110 },
];

const CASHFLOW_ANCHORS = [
  { concepts: ['NetIncomeLoss', 'ProfitLoss'], label: 'Net Income', section: 'operating', isBold: true, order: 10 },
  { concepts: ['DepreciationDepletionAndAmortization', 'DepreciationAndAmortization'], label: 'Depreciation & Amortization', section: 'operating', order: 11 },
  { concepts: ['ShareBasedCompensation', 'AllocatedShareBasedCompensationExpense'], label: 'Stock-Based Compensation', section: 'operating', order: 12 },
  { concepts: ['DeferredIncomeTaxExpenseBenefit'], label: 'Deferred Income Tax', section: 'operating', order: 13 },
  { concepts: ['NetCashProvidedByUsedInOperatingActivities', 'NetCashProvidedByUsedInOperatingActivitiesContinuingOperations'], label: 'Cash from Operations', section: 'operating', isBold: true, order: 20, keywords: ['NetCashProvidedByUsedInOperating'] },
  { concepts: ['PaymentsToAcquirePropertyPlantAndEquipment'], label: 'Capital Expenditures', section: 'investing', order: 30, keywords: ['PaymentsToAcquirePropertyPlant', 'CapitalExpenditure'] },
  { concepts: ['PaymentsToAcquireBusinessesNetOfCashAcquired', 'PaymentsToAcquireBusinessesAndInterestInAffiliates'], label: 'Acquisitions', section: 'investing', order: 31, keywords: ['PaymentsToAcquireBusiness'] },
  { concepts: ['NetCashProvidedByUsedInInvestingActivities', 'NetCashProvidedByUsedInInvestingActivitiesContinuingOperations'], label: 'Cash from Investing', section: 'investing', isBold: true, order: 40, keywords: ['NetCashProvidedByUsedInInvesting'] },
  { concepts: ['PaymentsOfDividends', 'PaymentsOfDividendsCommonStock'], label: 'Dividends Paid', section: 'financing', order: 50, keywords: ['PaymentsOfDividend'] },
  { concepts: ['PaymentsForRepurchaseOfCommonStock'], label: 'Share Repurchases', section: 'financing', order: 51, keywords: ['RepurchaseOfCommonStock'] },
  { concepts: ['ProceedsFromIssuanceOfCommonStock', 'ProceedsFromStockOptionsExercised'], label: 'Stock Issuance', section: 'financing', order: 52 },
  { concepts: ['NetCashProvidedByUsedInFinancingActivities', 'NetCashProvidedByUsedInFinancingActivitiesContinuingOperations'], label: 'Cash from Financing', section: 'financing', isBold: true, order: 60, keywords: ['NetCashProvidedByUsedInFinancing'] },
  { concepts: ['CashCashEquivalentsRestrictedCashAndRestrictedCashEquivalentsPeriodIncreaseDecreaseIncludingExchangeRateEffect', 'CashAndCashEquivalentsPeriodIncreaseDecrease', 'CashCashEquivalentsRestrictedCashAndRestrictedCashEquivalentsPeriodIncreaseDecreaseExcludingExchangeRateEffect'], label: 'Net Change in Cash', section: 'netChange', isBold: true, order: 70, keywords: ['CashEquivalentsPeriodIncreaseDecrease'] },
];

function getAnchors(statementType) {
  switch (statementType) {
    case 'income': return INCOME_ANCHORS;
    case 'balance': return BALANCE_ANCHORS;
    case 'cashflow': return CASHFLOW_ANCHORS;
    default: return INCOME_ANCHORS;
  }
}

// Main extraction function — hybrid: frames for periods, form+date for values
export function extractStatementData(facts, statementType, periodType = 'quarterly') {
  const allFacts = facts.facts || {};
  const isBalanceSheet = statementType === 'balance';
  const anchors = getAnchors(statementType);
  
  // Step 1: Determine target periods using frames
  const { quarterlyFrames, annualFrames, instantFrames } = getAvailableFramesFromFacts(facts);
  
  let targetFrames;
  if (periodType === 'annual') {
    if (isBalanceSheet) {
      let annualInstant = Array.from(instantFrames).filter(f => /^CY\d{4}I$/.test(f));
      if (annualInstant.length === 0) {
        annualInstant = Array.from(instantFrames).filter(f => /^CY\d{4}Q4I$/.test(f));
      }
      if (annualInstant.length === 0) {
        annualInstant = Array.from(instantFrames).filter(f => /^CY\d{4}Q[1-4]I$/.test(f));
      }
      targetFrames = annualInstant.sort().reverse();
    } else {
      targetFrames = Array.from(annualFrames).sort().reverse();
    }
  } else {
    targetFrames = isBalanceSheet
      ? Array.from(instantFrames).filter(f => /^CY\d{4}Q[1-4]I$/.test(f)).sort().reverse()
      : Array.from(quarterlyFrames).sort().reverse();
  }
  
  const frames = targetFrames.slice(0, 4);
  if (frames.length === 0) return [];
  
  // Convert frames to target end dates for form+date matching
  const targetPeriods = frames.map(f => frameToPeriod(f));
  
  // Build quarter lookup for fuzzy date matching (52/53 week fiscal years)
  const dateToQ = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-Q${Math.floor(dt.getMonth() / 3) + 1}`;
  };
  const targetQuarters = {};
  frames.forEach((f, i) => {
    targetQuarters[dateToQ(targetPeriods[i])] = { frame: f, period: targetPeriods[i], idx: i };
  });
  
  // Form filters
  const annualForms = ['10-K', '10-K/A', '20-F', '20-F/A'];
  const quarterlyForms = ['10-Q', '10-Q/A', '6-K', '6-K/A'];
  const targetForms = periodType === 'annual' ? annualForms : quarterlyForms;
  
  // Step 2: Index ALL concept values using DUAL approach
  // Primary: frame match. Fallback: form+date match (v1.22 method that actually works)
  const conceptValues = {};  // key → { frame → value }
  const conceptUnits = {};
  
  for (const [namespace, concepts] of Object.entries(allFacts)) {
    if (namespace === 'dei') continue;
    
    for (const [conceptName, conceptData] of Object.entries(concepts)) {
      if (!conceptData?.units) continue;
      
      const unitPriority = ['USD', 'EUR', 'ILS', 'GBP', 'USD/shares', 'shares', 'pure'];
      let values = null;
      let unitType = null;
      
      for (const unit of unitPriority) {
        if (conceptData.units[unit]?.length > 0) {
          values = conceptData.units[unit];
          unitType = unit;
          break;
        }
      }
      if (!values) continue;
      
      const key = (namespace === 'us-gaap' || namespace === 'ifrs-full')
        ? conceptName 
        : `${namespace}:${conceptName}`;
      
      // === PRIMARY: Frame-based matching (fast, reliable for income + balance sheet) ===
      for (const v of values) {
        if (!v.frame || !frames.includes(v.frame)) continue;
        if (!conceptValues[key]) conceptValues[key] = {};
        conceptValues[key][v.frame] = v.val;
        conceptUnits[key] = unitType;
      }
      
      // === FALLBACK: Form+date matching (v1.22 method — catches cash flow + fiscal calendar mismatches) ===
      // Only run if frame matching found fewer periods than we have target frames
      const existingPeriods = conceptValues[key] ? Object.keys(conceptValues[key]).length : 0;
      if (existingPeriods < frames.length) {
        // Find which target quarters are still missing
        const missingFrames = frames.filter(f => !conceptValues[key]?.[f]);
        if (missingFrames.length === 0) continue;
        
        // Collect form-matching values grouped by quarter
        const byQuarter = {};
        for (const v of values) {
          if (!v.end) continue;
          const vQ = dateToQ(v.end);
          const target = targetQuarters[vQ];
          if (!target) continue;
          if (conceptValues[key]?.[target.frame]) continue; // Already have this quarter
          
          const formMatch = targetForms.includes(v.form);
          const days = v.start ? (new Date(v.end) - new Date(v.start)) / (1000 * 60 * 60 * 24) : 0;
          const isStandalone = !isBalanceSheet && days > 0 && days <= 120;
          const isYTD = !isBalanceSheet && days > 120;
          
          if (!byQuarter[vQ]) byQuarter[vQ] = { standalone: null, ytd: null, any: null, frame: target.frame };
          
          // Priority: form-matched standalone > form-matched YTD > any value
          if (formMatch && isStandalone) {
            byQuarter[vQ].standalone = v.val;
          } else if (formMatch) {
            byQuarter[vQ].ytd = v.val;
          } else if (isBalanceSheet || isStandalone) {
            byQuarter[vQ].any = v.val;
          }
        }
        
        // Apply: prefer standalone > YTD > any
        for (const [, data] of Object.entries(byQuarter)) {
          const val = data.standalone ?? data.ytd ?? data.any;
          if (val !== null && val !== undefined) {
            if (!conceptValues[key]) conceptValues[key] = {};
            conceptValues[key][data.frame] = val;
            conceptUnits[key] = unitType;
          }
        }
      }
    }
  }
  
  const periodKeys = frames;
  
  // Step 3: Anchor matching (with IFRS + keyword fallback)
  const rows = [];
  const usedConcepts = new Set();
  
  for (const anchor of anchors) {
    let matchedConcept = null;
    
    // Check direct matches
    for (const c of anchor.concepts) {
      if (conceptValues[c] && Object.keys(conceptValues[c]).length > 0) {
        matchedConcept = c;
        break;
      }
    }
    
    // Check IFRS mapping
    if (!matchedConcept) {
      for (const c of anchor.concepts) {
        const ifrsName = IFRS_MAPPING[c];
        if (ifrsName && conceptValues[ifrsName] && Object.keys(conceptValues[ifrsName]).length > 0) {
          matchedConcept = ifrsName;
          break;
        }
      }
    }
    
    // Keyword search fallback for company-specific extensions
    if (!matchedConcept && anchor.keywords) {
      const allConcepts = Object.keys(conceptValues);
      for (const kw of anchor.keywords) {
        const kwLower = kw.toLowerCase();
        const match = allConcepts.find(c => c.toLowerCase().includes(kwLower) && !usedConcepts.has(c));
        if (match) { matchedConcept = match; break; }
      }
    }
    
    if (!matchedConcept) continue;
    
    for (const c of anchor.concepts) {
      usedConcepts.add(c);
      if (IFRS_MAPPING[c]) usedConcepts.add(IFRS_MAPPING[c]);
    }
    usedConcepts.add(matchedConcept);
    
    const vals = conceptValues[matchedConcept];
    const unitType = conceptUnits[matchedConcept];
    
    rows.push({
      concept: matchedConcept,
      label: anchor.label,
      order: anchor.order,
      isHeader: false, isSubtotal: false, isTotal: false,
      isPerShare: anchor.isPerShare || unitType === 'USD/shares',
      isShares: anchor.isShares || unitType === 'shares',
      sectionId: anchor.section, isChild: false,
      isBold: anchor.isBold || false,
      quarters: periodKeys.map(f => ({ period: frameToPeriod(f), value: vals[f] ?? null, frame: f }))
    });
  }
  
  // Step 4: Computed fallback rows
  const hasRow = (label) => rows.some(r => r.label === label);
  const getRowValues = (label) => {
    const row = rows.find(r => r.label === label);
    return row ? row.quarters : null;
  };
  
  // Computed Gross Profit
  if (!hasRow('Gross Profit') && statementType === 'income') {
    const revQ = getRowValues('Total Revenue');
    const costQ = getRowValues('Cost of Revenue');
    if (revQ && costQ) {
      const gpQuarters = periodKeys.map((f, i) => ({
        period: frameToPeriod(f),
        value: (revQ[i]?.value != null && costQ[i]?.value != null) ? revQ[i].value - costQ[i].value : null,
        frame: f, computed: true
      }));
      if (gpQuarters.some(q => q.value !== null)) {
        const costIdx = rows.findIndex(r => r.label === 'Cost of Revenue');
        rows.splice(costIdx >= 0 ? costIdx + 1 : rows.length, 0, {
          concept: '_computed_GrossProfit', label: 'Gross Profit',
          order: 30,
          isHeader: false, isSubtotal: false, isTotal: false,
          isPerShare: false, isShares: false,
          sectionId: 'gross', isChild: false, isBold: true,
          quarters: gpQuarters
        });
      }
    }
  }
  
  // Computed Total Liabilities
  if (!hasRow('Total Liabilities') && statementType === 'balance') {
    const currentLiab = conceptValues['LiabilitiesCurrent'];
    const ncLiab = conceptValues['LiabilitiesNoncurrent'];
    const liabAndEquity = conceptValues['LiabilitiesAndStockholdersEquity'];
    const equity = conceptValues['StockholdersEquity'] || conceptValues['StockholdersEquityIncludingPortionAttributableToNoncontrollingInterest'];
    
    let tlQ = null;
    if (currentLiab && ncLiab) {
      tlQ = periodKeys.map(f => ({
        period: frameToPeriod(f),
        value: (currentLiab[f] != null && ncLiab[f] != null) ? currentLiab[f] + ncLiab[f] : null,
        frame: f, computed: true
      }));
    }
    if ((!tlQ || !tlQ.some(q => q.value !== null)) && liabAndEquity && equity) {
      tlQ = periodKeys.map(f => ({
        period: frameToPeriod(f),
        value: (liabAndEquity[f] != null && equity[f] != null) ? liabAndEquity[f] - equity[f] : null,
        frame: f, computed: true
      }));
    }
    if (tlQ && tlQ.some(q => q.value !== null)) {
      const ncIdx = rows.findIndex(r => r.label === 'Non-Current Liabilities');
      const ltdIdx = rows.findIndex(r => r.label === 'Long-Term Debt');
      const clIdx = rows.findIndex(r => r.label === 'Total Current Liabilities');
      const insertAt = ncIdx >= 0 ? ncIdx + 1 : (ltdIdx >= 0 ? ltdIdx + 1 : (clIdx >= 0 ? clIdx + 1 : rows.length));
      rows.splice(insertAt, 0, {
        concept: '_computed_TotalLiabilities', label: 'Total Liabilities',
        order: 80,
        isHeader: false, isSubtotal: false, isTotal: false,
        isPerShare: false, isShares: false,
        sectionId: 'totalLiab', isChild: false, isBold: true,
        quarters: tlQ
      });
    }
  }
  
  // Debug logging
  if (statementType === 'cashflow' || statementType === 'balance') {
    const indexed = Object.keys(conceptValues).length;
    const withData = Object.values(conceptValues).filter(v => Object.keys(v).length >= frames.length).length;
    console.log(`[${statementType.toUpperCase()}] ${rows.length} rows, ${indexed} concepts indexed (${withData} with full data), periods: ${frames.slice(0,2).join(', ')}`);
  }
  
  return rows;
}

// Get available periods from the data
export function getAvailablePeriods(facts, periodType = 'quarterly') {
  const { quarterlyFrames, annualFrames, instantFrames } = getAvailableFramesFromFacts(facts);
  
  let frames;
  if (periodType === 'annual') {
    frames = Array.from(annualFrames).sort().reverse();
    if (frames.length === 0) {
      // Try annual instants first, then Q4 instants
      frames = Array.from(instantFrames).filter(f => /^CY\d{4}I$/.test(f)).sort().reverse();
      if (frames.length === 0) {
        frames = Array.from(instantFrames).filter(f => /^CY\d{4}Q4I$/.test(f)).sort().reverse();
      }
    }
  } else {
    frames = Array.from(quarterlyFrames).sort().reverse();
    if (frames.length === 0) {
      frames = Array.from(instantFrames).filter(f => /^CY\d{4}Q[1-4]I$/.test(f)).sort().reverse();
    }
  }
  
  return frames.slice(0, 8).map(f => frameToPeriod(f));
}

// Check if a company has quarterly XBRL data
export function hasQuarterlyData(facts) {
  const { quarterlyFrames, instantFrames } = getAvailableFramesFromFacts(facts);
  const hasQDuration = Array.from(quarterlyFrames).some(f => /^CY\d{4}Q[1-4]$/.test(f));
  const hasQInstant = Array.from(instantFrames).some(f => /^CY\d{4}Q[1-4]I$/.test(f));
  return hasQDuration || hasQInstant;
}

// Format number for display - SEC XBRL data is in actual dollars
// We display in millions (M) or thousands (K) for readability
export function formatValue(value, options = {}) {
  if (value === null || value === undefined) return '—';
  
  const { isPerShare, isShares } = options;
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  
  let formatted;
  
  if (isPerShare) {
    // Per share: show 2 decimal places with dollar sign
    formatted = '$' + absValue.toFixed(2);
  } else if (isShares) {
    // Shares: show in millions
    if (absValue >= 1000000) {
      formatted = (absValue / 1000000).toFixed(1) + 'M';
    } else if (absValue >= 1000) {
      formatted = (absValue / 1000).toFixed(1) + 'K';
    } else {
      formatted = absValue.toLocaleString('en-US');
    }
  } else {
    // Currency: SEC data is in actual dollars
    // Display in B/M/K for readability
    if (absValue >= 1000000000) {
      formatted = '$' + (absValue / 1000000000).toFixed(2) + 'B';
    } else if (absValue >= 1000000) {
      formatted = '$' + (absValue / 1000000).toFixed(1) + 'M';
    } else if (absValue >= 1000) {
      formatted = '$' + (absValue / 1000).toFixed(1) + 'K';
    } else {
      formatted = '$' + absValue.toLocaleString('en-US');
    }
  }
  
  // Accounting format: negatives in parentheses
  if (isNegative) {
    return '(' + formatted + ')';
  }
  return formatted;
}

// Calculate divergence percentage between two values
export function calculateDivergence(val1, val2) {
  if (val1 === null || val1 === undefined || val2 === null || val2 === undefined) {
    return null;
  }
  if (val1 === 0 && val2 === 0) return 0;
  if (val1 === 0 || val2 === 0) return null; // Can't calculate % diff with zero
  
  // Calculate percentage difference
  const diff = ((val1 - val2) / Math.abs(val2)) * 100;
  return diff;
}

// Calculate margin as percentage of revenue
// Returns the margin % or null if can't calculate
export function calculateMarginPercent(metricValue, revenueValue) {
  if (!metricValue || !revenueValue || revenueValue === 0) return null;
  return (metricValue / revenueValue) * 100;
}

// Calculate vs industry benchmark
// Returns difference from benchmark (e.g., +13% means 13 points above benchmark)
export function calculateVsIndustry(actualPercent, benchmarkPercent) {
  if (actualPercent === null || benchmarkPercent === null) return null;
  return actualPercent - benchmarkPercent;
}
